import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useSupabaseAuth } from '@/hooks/useSupabaseAuth';
import { userService, type UserProfile } from '@/services/userService';
import { supabase } from '@/integrations/supabase/client';

export type AccountType = 'talent' | 'brand' | 'venture' | 'agent';

interface UserContextType {
  currentUser: UserProfile | null;
  isLoggedIn: boolean;
  accountType: AccountType | null;
  setupCompleted: boolean;
  betaAccess: boolean;
  login: (user: UserProfile) => void;
  logout: () => void;
  setAccountType: (type: AccountType) => void;
  loading: boolean;
  setAccountTypeFromAuth: (type: AccountType) => void;
  refreshUserData: () => Promise<void>;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user: authUser, loading: authLoading } = useSupabaseAuth();
  const [currentUser, setCurrentUser] = useState<UserProfile | null>(null);
  const [accountType, setAccountType] = useState<AccountType | null>(null);
  const [setupCompleted, setSetupCompleted] = useState(false);
  const [betaAccess, setBetaAccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const [timedOut, setTimedOut] = useState(false);
  const lastLoadedUserId = useRef<string | null>(null);

  // Timeout fallback: if auth takes >5s, stop blocking and let ProtectedRoute handle redirect
  useEffect(() => {
    const timer = setTimeout(() => {
      console.log('UserContext: Auth initialization timed out after 5s, unblocking render');
      setTimedOut(true);
    }, 5000);
    return () => clearTimeout(timer);
  }, []);

  // Load current user data from Supabase
  useEffect(() => {
    const loadData = async () => {
      try {
        // CRITICAL: Check for signup verification flag FIRST
        const isSignupVerification = sessionStorage.getItem('isSignupVerification') === 'true';
        if (isSignupVerification) {
          console.log('UserContext: Signup verification flag detected, skipping profile load for fresh start');
          setAccountType(null);
          setSetupCompleted(false);
          setBetaAccess(false);
          setCurrentUser(null);
          localStorage.removeItem('accountType');
          localStorage.removeItem('currentUser');
          setLoading(false);
          setIsInitialized(true);
          return;
        }
        
        if (authUser) {
          // Skip redundant loads — use ref only (no stale closure dependency)
          if (lastLoadedUserId.current === authUser.id) {
            console.log('UserContext: Profile already loaded for this user, skipping reload');
            setLoading(false);
            setIsInitialized(true);
            return;
          }
          
          // Only set loading for genuine first loads
          setLoading(true);
          
          console.log('UserContext: User authenticated, loading profile...');
          // Ensure profile and beta application exist before fetching data
          await supabase.rpc('ensure_profile_and_beta_application', {
            _email: authUser.email || '',
            _display_name: authUser.user_metadata?.display_name || authUser.email?.split('@')[0] || ''
          });
          
          const userProfile = await userService.getProfileByUserId(authUser.id);
          if (userProfile) {
            console.log('UserContext: Loaded profile', {
              account_type: userProfile.account_type,
              setup_completed: userProfile.setup_completed,
              beta_access: userProfile.beta_access
            });
            
            // CRITICAL: If user has no account_type, they need role selection
            // Treat them as a new signup to force redirect to role-selection
            if (!userProfile.account_type && !userProfile.setup_completed) {
              console.log('UserContext: User has no account_type, treating as new signup for role-selection');
              sessionStorage.setItem('isSignupVerification', 'true');
              setAccountType(null);
              setSetupCompleted(false);
              setBetaAccess(false);
              setCurrentUser(userProfile); // Still set the user so we know they're authenticated
              localStorage.removeItem('accountType');
              localStorage.removeItem('currentUser');
              setLoading(false);
              setIsInitialized(true);
              return; // Exit early, let ProtectedRoute handle redirect
            }
            
            lastLoadedUserId.current = authUser.id;
            setCurrentUser(userProfile);
            setAccountType(userProfile.account_type as AccountType | null);
            setSetupCompleted(userProfile.setup_completed || false);
            setBetaAccess(userProfile.beta_access || false);
            // Guard against storing "null" string in localStorage
            if (userProfile.account_type) {
              localStorage.setItem('accountType', userProfile.account_type);
            } else {
              localStorage.removeItem('accountType');
            }
            localStorage.setItem('currentUser', JSON.stringify(userProfile));
          }
        } else {
          console.log('UserContext: No authenticated user, clearing state');
          // Not authenticated, clear current user
          lastLoadedUserId.current = null;
          setCurrentUser(null);
          setAccountType(null);
          setSetupCompleted(false);
          setBetaAccess(false);
          localStorage.removeItem('currentUser');
          localStorage.removeItem('accountType');
        }
      } catch (error) {
        console.error('UserContext: Error loading data:', error);
      } finally {
        setLoading(false);
        setIsInitialized(true);
      }
    };

    if (!authLoading) {
      console.log('UserContext: Auth loading complete, starting data load');
      loadData();
    }
  }, [authUser, authLoading]);

  const login = (user: UserProfile) => {
    setCurrentUser(user);
    localStorage.setItem('currentUser', JSON.stringify(user));
  };

  const logout = () => {
    setCurrentUser(null);
    localStorage.removeItem('currentUser');
    localStorage.removeItem('accountType');
  };

  const handleSetAccountType = (type: AccountType) => {
    // Only allow account type changes if setup is not completed
    if (!setupCompleted) {
      setAccountType(type);
      localStorage.setItem('accountType', type);
    }
  };

  const setAccountTypeFromAuth = (type: AccountType) => {
    setAccountType(type);
    localStorage.setItem('accountType', type);
  };

  // Refresh user data - useful after onboarding completion
  const refreshUserData = async () => {
    if (authUser) {
      const userProfile = await userService.getProfileByUserId(authUser.id);
      if (userProfile) {
        console.log('UserContext: Refreshed profile', {
          account_type: userProfile.account_type,
          setup_completed: userProfile.setup_completed,
          beta_access: userProfile.beta_access
        });
        setCurrentUser(userProfile);
        setAccountType(userProfile.account_type as AccountType | null);
        setSetupCompleted(userProfile.setup_completed || false);
        setBetaAccess(userProfile.beta_access || false);
        // Guard against storing "null" string in localStorage
        if (userProfile.account_type) {
          localStorage.setItem('accountType', userProfile.account_type);
        } else {
          localStorage.removeItem('accountType');
        }
        localStorage.setItem('currentUser', JSON.stringify(userProfile));
      }
    }
  };

  const value = {
    currentUser,
    isLoggedIn: !!currentUser,
    accountType,
    setupCompleted,
    betaAccess,
    login,
    logout,
    setAccountType: handleSetAccountType,
    loading,
    setAccountTypeFromAuth,
    refreshUserData
  };

  // CRITICAL: Check for signup verification BEFORE blocking rendering
  // If signup verification is detected, don't block - let ProtectedRoute handle the redirect
  const isSignupVerificationCheck = sessionStorage.getItem('isSignupVerification') === 'true';
  
  // Don't render children until initialization is complete
  // BUT: Don't block if this is a signup verification - we need ProtectedRoute to run immediately
  if ((!isInitialized || authLoading || loading) && !isSignupVerificationCheck && !timedOut) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="text-muted-foreground">Loading user data...</p>
        </div>
      </div>
    );
  }

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};